import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import ConnectionCheckMeter, { type AuthBudget } from '@/components/account/ConnectionCheckMeter'
import { KeyRound, Loader2, AlertCircle } from 'lucide-react'

interface ReconnectTarget {
  id: number
  name: string
  clientIdMasked: string
  environment: string
  /** Null when the hourly-check meter itself is unreadable — then show no meter, block nothing. */
  authBudget: AuthBudget | null
}

interface ReconnectCredentialModalProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  connection: ReconnectTarget | null
  /** Called with the connection id when this save took a broken connection back to healthy. */
  onHealed?: (connectionId: number) => void
  onClose: () => void
}


// WS-F3: reconnect / update secret. Write-only secret field; the client id and environment
// are shown read-only (changing which Eviivo account serves a property is the separate
// "Move properties" flow).
//
// The secret is verified against Eviivo BEFORE it is saved, and a verified secret on a
// SUSPENDED connection now heals it outright (health: "healthy", healed: true) — it no longer
// lands in a degraded holding pattern waiting for the next probe. So a heal is reported as
// the resolution it is, in the server's own words.
export default function ReconnectCredentialModal({ settingsPath, connection, onHealed, onClose }: ReconnectCredentialModalProps) {
  const { addToast } = useToast()
  const [clientSecret, setClientSecret] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset local state whenever a different connection opens the modal.
  React.useEffect(() => {
    setClientSecret('')
    setError(null)
    setSaving(false)
  }, [connection?.id])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!connection || clientSecret.trim() === '') return
    setError(null)
    setSaving(true)

    try {
      const response = await apiFetch(`${settingsPath}/pms-connections/${connection.id}/secret`, {
        method: 'PATCH',
        body: JSON.stringify({ client_secret: clientSecret }),
      })
      const data = await response.json()

      if (!response.ok) {
        setError(data.errors?.join(', ') || 'We could not verify that secret. Please check it and try again.')
        return
      }

      if (data.healed) onHealed?.(connection.id)

      addToast({
        variant: 'success',
        title: data.healed ? 'Connection restored' : 'Secret updated',
        description: data.healed
          ? 'Your rate updates are resuming now.'
          : 'The new secret is verified and saved.',
      })
      onClose()
      // Refresh the page props so the health badge reflects the new state.
      router.reload({ only: ['connections'] })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={connection !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Update secret</DialogTitle>
          <DialogDescription>
            Re-enter the client secret for {connection?.name}. We&apos;ll verify it with your PMS before saving.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            {/* Read-only identity */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Client ID</Label>
                <div className="flex h-9 items-center rounded-lg border border-border/60 bg-muted/50 px-3 font-mono text-xs tabular-nums text-muted-foreground">
                  {connection?.clientIdMasked}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Environment</Label>
                <div className="flex h-9 items-center rounded-lg border border-border/60 bg-muted/50 px-3 text-xs capitalize text-muted-foreground">
                  {connection?.environment}
                </div>
              </div>
            </div>

            {/* Write-only secret */}
            <div className="space-y-2">
              <Label htmlFor="reconnect-secret">New client secret</Label>
              <Input
                id="reconnect-secret"
                type="password"
                autoComplete="off"
                placeholder="Enter the client secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                Verifying spends one connection check. A secret that works restores the connection straight away.
              </p>
              <ConnectionCheckMeter budget={connection?.authBudget} />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-lg"
              disabled={saving || clientSecret.trim() === ''}
              aria-busy={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <KeyRound className="mr-1.5 h-3.5 w-3.5" />
                  Verify &amp; save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
